import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import {
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  TextField,
  Typography
} from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import clsx from 'clsx'
import { useFormikContext } from 'formik'
import moment from 'moment'
import { loadEventCircuitCategoryResults } from 'state/modules/championships'
import { STRING_EMPTY } from 'utils/constants'

import { SectionBlock } from '../SectionBlock'

import { useStyles } from './EventsSection.style'

const EventsSection = ({ organizationOptions, eventOptions, loading }) => {
  const classes = useStyles()
  const dispatch = useDispatch()

  const { values, errors, setFieldValue, setValues, handleBlur } = useFormikContext()

  const [activeEdit, setActiveEdit] = useState(!!values.events.length)

  const onInputChange = (setFieldValue) => (event, value, reason) => {
    value = (value ?? STRING_EMPTY).trim()

    setFieldValue('eventsName', value)
  }

  const onOrganizationInputChange = (setFieldValue) => (event, value, reason) => {
    value = (value ?? STRING_EMPTY).trim()

    setFieldValue('organizationName', value)
  }

  const onEventsChange = async (_, optionsSelected) => {
    const eventIds = optionsSelected.map((e) => e.value)

    const data = await dispatch(loadEventCircuitCategoryResults(values.id, eventIds))
    setValues((values) => ({
      ...values,
      events: optionsSelected,
      excludedDistances: Object.values(values.excludedDistances ?? {})
        ?.filter((d) => d.checked && !data.distances.some((x) => x.name === d.name))
        .reduce(
          (acc, curr) => ({
            ...acc,
            [curr.name]: {
              name: curr.name,
              checked: false
            }
          }),
          { ...values.excludedDistances }
        ),
      excludedCategories: Object.values(values.excludedCategories ?? {})
        ?.filter((d) => d.checked && !data.categories.some((x) => x.name === d.name))
        .reduce(
          (acc, curr) => ({
            ...acc,
            [curr.name]: {
              name: curr.name,
              checked: false
            }
          }),
          { ...values.excludedCategories }
        )
    }))
  }

  return (
    <SectionBlock title='Eventos'>
      {activeEdit ? (
        <div className={classes.eventsContainer}>
          <FormControl variant='outlined' className={classes.formControl}>
            <Autocomplete
              id='organization-autocomplete'
              options={organizationOptions}
              getOptionLabel={(option) => option.label || option.name}
              value={values.organizationSelected}
              fullWidth
              onInputChange={onOrganizationInputChange(setFieldValue)}
              onChange={(_, optionsSelected) =>
                setFieldValue('organizationSelected', optionsSelected)
              }
              disableClearable
              loading={loading}
              filterSelectedOptions
              filterOptions={(options) =>
                options.filter((option) =>
                  option.label
                    .toLowerCase()
                    .trim()
                    .includes(values.organizationName.trim().toLowerCase())
                )
              }
              className={classes.combo}
              clearText='Quitar todo'
              openText='Abrir desplegable'
              closeText='Cerrar desplegable'
              loadingText={<Typography align='center'> Cargando...</Typography>}
              noOptionsText={<Typography align='center'> Sin opciones</Typography>}
              renderInput={(params) => (
                <TextField
                  {...params}
                  className={clsx(classes.input, classes.comboInput)}
                  name='organizationName'
                  label='Nombre de la organización'
                  variant='outlined'
                  helperText={'Escriba el nombre de la organización'}
                />
              )}
            />
          </FormControl>

          <FormControl variant='outlined' className={classes.formControl}>
            <Autocomplete
              multiple
              id='events-autocomplete'
              options={eventOptions}
              getOptionLabel={(option) => option.label}
              value={values.events}
              fullWidth
              filterOptions={(options) =>
                options.filter(
                  (option) =>
                    moment(option.from).isSameOrAfter(values.fromDate) &&
                    moment(option.to).isSameOrBefore(values.toDate) &&
                    option.organizations.some(
                      (x) =>
                        x.organizationId ===
                        (values.organizationSelected?.value ?? values.organizationSelected?.id)
                    ) &&
                    option.label
                      .toLowerCase()
                      .trim()
                      .includes(values.eventName.trim().toLowerCase())
                )
              }
              loading={loading}
              onInputChange={onInputChange(setFieldValue)}
              onChange={onEventsChange}
              getOptionSelected={(option) => values.events.some((o) => o.value === option.value)}
              filterSelectedOptions
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <div key={option.value} className={classes.eventOption}>
                    <Chip
                      variant='outlined'
                      label={option.label}
                      title={option.label}
                      {...getTagProps({ index })}
                      color='primary'
                      className={classes.option}
                    />

                    <FormControlLabel
                      className={classes.controlLabel}
                      control={
                        <Checkbox
                          color='primary'
                          size='small'
                          checked={
                            !!values.doubleScoreEvents && values.doubleScoreEvents[option.value]
                          }
                          onChange={(e) =>
                            setFieldValue('doubleScoreEvents', {
                              ...values.doubleScoreEvents,
                              [option.value]: e.target.checked
                            })
                          }
                          name='doubleScoreEvents'
                          onBlur={handleBlur}
                        />
                      }
                      label={
                        <Typography variant='h6' className={classes.checkboxLabel}>
                          Doble puntaje
                        </Typography>
                      }
                    />
                  </div>
                ))
              }
              className={classes.combo}
              clearText='Quitar todo'
              openText='Abrir desplegable'
              closeText='Cerrar desplegable'
              loadingText={<Typography align='center'> Cargando...</Typography>}
              noOptionsText={<Typography align='center'> Sin opciones</Typography>}
              renderInput={(params) => (
                <TextField
                  {...params}
                  className={clsx(classes.input, classes.comboInput)}
                  name='disciplines'
                  label='Nombre del evento'
                  variant='outlined'
                  error={!!errors.events}
                  helperText={errors.events || 'Escriba el nombre del evento'}
                />
              )}
            />
          </FormControl>
        </div>
      ) : (
        <Typography
          color='primary'
          variant='h6'
          className={classes.addLink}
          onClick={() => setActiveEdit(true)}>
          +Añadir evento/s
        </Typography>
      )}
    </SectionBlock>
  )
}

export default EventsSection
